/**
 * Leaflet DivIcon builders + Recenter helper for the Nearby map.
 * Kept pure (no hooks except Recenter's useMap) so `NearbyMap` and any
 * future map surfaces can re-use them.
 */
import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// Sniffies-style avatar-in-a-pin marker.
export function buildAvatarIcon({ avatar_url, is_live, online_now, is_verified, ambassador_status, ambassador_tier, pin_type }) {
  if (pin_type === "fuzzy") return buildFuzzyIcon({ avatar_url });
  // Approved ambassadors get a golden ring override — the ring color is
  // tier-scaled (Gold → Silver → Bronze). Live/online rings still win because
  // a live host is time-sensitive information the pin has to show.
  const isAmbassador = ambassador_status === "approved" && !!ambassador_tier;
  const ambassadorColor = ambassador_tier === "gold"
    ? "#fbbf24"
    : ambassador_tier === "silver"
    ? "#d4d4d8"
    : "#b45309"; // bronze
  const ring = is_live
    ? "box-shadow: 0 0 0 3px #f43f5e, 0 0 24px rgba(244,63,94,0.75); animation: pandax-pin-pulse 1.6s infinite;"
    : online_now
    ? "box-shadow: 0 0 0 3px #10b981, 0 0 12px rgba(16,185,129,0.5);"
    : isAmbassador
    ? `box-shadow: 0 0 0 3px ${ambassadorColor}, 0 0 18px ${ambassadorColor}80;`
    : "box-shadow: 0 0 0 2px rgba(255,255,255,0.6);";
  const badge = is_verified
    ? `<div style="position:absolute;bottom:-2px;right:-2px;background:#f59e0b;width:14px;height:14px;border-radius:9999px;border:2px solid #0a0a0a;"></div>`
    : "";
  // Small sparkle badge in the top-right for ambassadors that aren't live —
  // when live, the "Live" pill on top takes priority visually.
  const ambassadorBadge = isAmbassador && !is_live
    ? `<div style="position:absolute;top:-2px;right:-2px;background:${ambassadorColor};color:#0a0a0a;width:16px;height:16px;border-radius:9999px;border:2px solid #0a0a0a;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:900;">★</div>`
    : "";
  const liveLabel = is_live
    ? `<div style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);background:#f43f5e;color:#fff;font-size:9px;font-weight:700;letter-spacing:0.15em;padding:1px 6px;border-radius:9999px;text-transform:uppercase;">Live</div>`
    : "";
  const html = `
    <div style="position:relative;width:44px;height:44px;">
      ${liveLabel}
      <img src="${avatar_url || ""}" style="width:44px;height:44px;border-radius:9999px;object-fit:cover;background:#111;${ring}" onerror="this.style.background='#f43f5e'" />
      ${badge}
      ${ambassadorBadge}
    </div>
  `;
  return L.divIcon({
    className: `pandax-avatar-marker is-exact${is_live ? " has-live" : ""}${isAmbassador ? " is-ambassador" : ""}`,
    html,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -22],
  });
}

// You-are-here marker
export function buildMeIcon() {
  return L.divIcon({
    className: "pandax-me-marker",
    html: `
      <div style="position:relative;width:22px;height:22px;">
        <div style="position:absolute;inset:-14px;border-radius:9999px;background:rgba(59,130,246,0.25);animation:pandax-me-pulse 2s infinite;"></div>
        <div style="width:22px;height:22px;border-radius:9999px;background:#3b82f6;border:3px solid #fff;box-shadow:0 4px 10px rgba(0,0,0,0.4);"></div>
      </div>
    `,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

// Cluster icon — dark pill with pin count, colored ring when it contains a live pin.
export function buildClusterIcon(cluster) {
  const count = cluster.getChildCount();
  let liveInside = false;
  try {
    for (const m of cluster.getAllChildMarkers()) {
      if (m.options?.icon?.options?.className?.includes("has-live")) {
        liveInside = true;
        break;
      }
    }
  } catch (leafletErr) {
    // Defensive against a future leaflet upgrade changing the internal
    // marker-cluster API (`getAllChildMarkers` / `options.icon.options`).
    // If it throws we degrade to a neutral cluster ring (no live pulse)
    // rather than crashing the whole map. Log once per session so ops can
    // catch upstream breakage the next time we bump leaflet.
    if (!window.__pandax_leaflet_cluster_warned) {
      window.__pandax_leaflet_cluster_warned = true;
      console.warn("[mapIcons] leaflet cluster inspection failed — check leaflet version:", leafletErr);
    }
  }
  const size = count < 10 ? 40 : count < 50 ? 48 : 58;
  const ring = liveInside
    ? "box-shadow:0 0 0 3px #f43f5e,0 0 20px rgba(244,63,94,0.6);animation:pandax-pin-pulse 1.6s infinite;"
    : "box-shadow:0 0 0 2px rgba(255,255,255,0.7),0 4px 10px rgba(0,0,0,0.35);";
  return L.divIcon({
    className: "pandax-cluster",
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:9999px;background:linear-gradient(135deg,#111,#3f0f1a);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-family:inherit;font-size:${count < 100 ? 14 : 12}px;${ring}">
        ${count}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Handle programmatic re-center — pans the map when `coords` changes.
export function Recenter({ coords, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (coords) map.flyTo([coords.lat, coords.lng], zoom ?? map.getZoom(), { duration: 0.8 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords]);
  return null;
}

// City-level approximate pin for recently active members who are NOT checked
// in: smaller, dimmed, dashed ring + "~" tag. Never implies an exact position.
export function buildFuzzyIcon({ avatar_url }) {
  const html = `
    <div style="position:relative;width:34px;height:34px;opacity:0.72;" title="Approximate · city-level">
      <img src="${avatar_url || ""}" style="width:34px;height:34px;border-radius:9999px;object-fit:cover;background:#111;filter:saturate(0.6);border:2px dashed rgba(255,255,255,0.75);box-shadow:0 0 0 2px rgba(10,10,10,0.9);" onerror="this.style.background='#52525b'" />
      <div style="position:absolute;top:-7px;left:50%;transform:translateX(-50%);background:#27272a;color:#d4d4d8;font-size:9px;font-weight:700;letter-spacing:0.12em;padding:0 5px;border-radius:9999px;border:1px solid rgba(255,255,255,0.25);">~ city</div>
    </div>`;
  return L.divIcon({ className: "pandax-avatar-marker is-fuzzy", html, iconSize: [34, 34], iconAnchor: [17, 17] });
}
